import {
  Building,
  Car,
  ChevronLeft,
  ChevronRight,
  Filter,
  MapPin,
  Search,
  Utensils,
} from "lucide-react";
import { ReactElement, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import AppNavbar from "@/components/common/AppNavbar";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { CitiesService, City } from "@/services/landing/citiesService";
import {
  CityRequest,
  CityRequestsService,
} from "@/services/landing/cityRequestsService";
import { fetchHeroBackgrounds } from "@/services/landing/heroBackgroundService";
export default function Cities() {
  const [cities, setCities] = useState<City[]>([]);
  const [comingSoonCities, setComingSoonCities] = useState<CityRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [featuredSlideIndex, setFeaturedSlideIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [comingSoonIndex, setComingSoonIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilter, setSelectedFilter] = useState<null | string>(null);
  const [heroBackground, setHeroBackground] = useState<null | {
    imageAlt: string;
    imageUrl: string;
  }>(null);
  const [heroLoading, setHeroLoading] = useState(true);
  const citiesPerPage = 9;
  const navigate = useNavigate();

  useEffect(() => {
    fetchHeroBackgrounds("cities").then((data) => {
      setHeroBackground(data);
      setHeroLoading(false);
    });
  }, []);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        setLoading(true);
        const data = await CitiesService.getAll();
        // Filter active cities (not hidden)
        const activeCities = data.filter(
          (city) => city.status === "active" && !city.hidden,
        );
        setCities(activeCities);
      } catch (error) {
        console.error("Failed to load cities:", error);
        setCities([]);
      } finally {
        setLoading(false);
      }
    };

    const fetchCityRequests = async () => {
      try {
        const requests = await CityRequestsService.getAll();
        console.log("All city requests:", requests);
        // Filter approved city requests for Coming Soon section
        const approvedRequests = requests.filter(
          (request) => request.status === "Approved",
        );
        console.log("Approved requests:", approvedRequests);
        setComingSoonCities(approvedRequests);
      } catch (error) {
        console.error("Failed to load city requests:", error);
        setComingSoonCities([]);
      }
    };

    fetchCities();
    fetchCityRequests();
  }, []);

  // Auto-scroll Coming Soon carousel with smooth infinite loop
  useEffect(() => {
    if (comingSoonCities.length > 4) {
      const interval = setInterval(() => {
        setComingSoonIndex((prevIndex) => {
          const nextIndex = prevIndex + 1;
          if (nextIndex >= comingSoonCities.length) {
            return 0;
          }
          return nextIndex;
        });
      }, 3000); // Change slide every 3 seconds

      return () => clearInterval(interval);
    }
  }, [comingSoonCities.length]);

  const serviceIcons: Record<string, ReactElement> = {
    business: <Building className="text-success" size={16} />,
    eats: <Utensils className="text-secondary" size={16} />,
    rides: <Car className="text-primary" size={16} />,
    scooters: <MapPin className="text-accent" size={16} />,
  };

  const formatPopulation = (count: string, unit: string) => {
    return `${count}${unit}`;
  };

  const formatNumber = (num: string) => {
    const number = parseInt(num);
    if (number >= 1000000) return `${(number / 1000000).toFixed(1)}M`;
    if (number >= 1000) return `${(number / 1000).toFixed(0)}K`;
    return num;
  };

  // Filter cities based on search and filters
  const filteredCities = cities.filter((city) => {
    // Search filter
    const matchesSearch =
      searchTerm === "" ||
      city.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      city.province.toLowerCase().includes(searchTerm.toLowerCase());

    // Service filter
    let matchesServiceFilter = true;
    if (selectedFilter && selectedFilter !== "all") {
      if (selectedFilter === "featured") {
        matchesServiceFilter = city.featured;
      } else if (selectedFilter === "recent") {
        const currentYear = new Date().getFullYear();
        matchesServiceFilter = parseInt(city.sinceYear) >= currentYear - 2;
      } else {
        matchesServiceFilter = city.servicesAvailable.some(
          (service) => service.toLowerCase() === selectedFilter.toLowerCase(),
        );
      }
    }

    return matchesSearch && matchesServiceFilter;
  });

  // Pagination helpers
  const totalPages = Math.ceil(filteredCities.length / citiesPerPage);
  const indexOfLastCity = currentPage * citiesPerPage;
  const indexOfFirstCity = indexOfLastCity - citiesPerPage;
  const currentCities = filteredCities.slice(indexOfFirstCity, indexOfLastCity);

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedFilter]);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    document
      .getElementById("all-cities")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  // Featured cities slider helpers
  const featuredCities = filteredCities.filter((city) => city.featured);
  const showSlider = featuredCities.length > 3;
  const visibleFeaturedCities = showSlider
    ? featuredCities.slice(featuredSlideIndex, featuredSlideIndex + 3)
    : featuredCities;

  const handleNextSlide = () => {
    if (featuredSlideIndex + 3 < featuredCities.length) {
      setFeaturedSlideIndex(featuredSlideIndex + 1);
    }
  };

  const handlePrevSlide = () => {
    if (featuredSlideIndex > 0) {
      setFeaturedSlideIndex(featuredSlideIndex - 1);
    }
  };

  return (
    <>
      <AppNavbar />

      <div className="min-h-screen bg-base-200">
        <Breadcrumbs />

        {/* Hero Section */}
        <section className="relative -mt-16 overflow-hidden pt-16">
          {/* Background Layer */}
          <div className="absolute inset-0 opacity-40">
            {heroLoading ? (
              <div className="relative h-full w-full overflow-hidden bg-base-200">
                <div
                  className="animate-shimmer absolute inset-0 bg-gradient-to-r from-base-200 via-base-100 to-base-200"
                  style={{ backgroundSize: "200% 100%" }}
                />
              </div>
            ) : heroBackground?.imageUrl ? (
              <img
                alt={heroBackground?.imageAlt || "Hero background"}
                className="h-full w-full object-cover"
                src={heroBackground.imageUrl}
              />
            ) : (
              <div className="h-full w-full bg-gradient-to-br from-base-200 to-base-100" />
            )}
          </div>

          {/* Content Layer */}
          <div className="relative mx-auto max-w-7xl px-4 py-24">
            <div className="text-center">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-primary-content/20 px-4 py-2">
                <MapPin size={20} />
                <span className="font-medium">
                  {loading
                    ? "Loading..."
                    : `Serving ${cities.length} Cities Across Africa`}
                </span>
              </div>

              <h1 className="mb-6 text-5xl font-bold leading-tight lg:text-6xl">
                Connecting <span className="text-primary">communities</span>{" "}
                across Africa
              </h1>

              <p className="mx-auto mb-8 max-w-3xl text-xl leading-relaxed opacity-90">
                From South to North, Hareeng is transforming how people move,
                eat, and connect in cities across the country. Discover where we
                operate and what services are available in your city.
              </p>

              <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
                <button
                  className="btn btn-primary btn-lg"
                  onClick={() => {
                    document
                      .getElementById("search-city")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  <Search size={20} />
                  Find Your City
                </button>
                <button
                  className="btn btn-outline btn-lg"
                  onClick={() => navigate("/pages/city-request")}
                >
                  Request New City
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Search and Filter */}
        <section className="bg-base-100 py-8" id="search-city">
          <div className="mx-auto max-w-7xl px-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="max-w-md flex-1">
                <div className="input-group">
                  <input
                    className="input input-bordered w-full"
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search cities..."
                    type="text"
                    value={searchTerm}
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  className={`btn btn-sm ${
                    selectedFilter === null || selectedFilter === "all"
                      ? "btn-primary"
                      : "btn-outline"
                  }`}
                  onClick={() => setSelectedFilter(null)}
                >
                  <Filter size={16} />
                  All Services
                </button>
                <button
                  className={`btn btn-sm ${
                    selectedFilter === "featured" ? "btn-primary" : "btn-ghost"
                  }`}
                  onClick={() => setSelectedFilter("featured")}
                >
                  Major Cities
                </button>
                <button
                  className={`btn btn-sm ${
                    selectedFilter === "recent" ? "btn-primary" : "btn-ghost"
                  }`}
                  onClick={() => setSelectedFilter("recent")}
                >
                  Recent
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Cities */}
        <section className="py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">Major Cities</h2>
              <p className="text-xl text-base-content/70">
                Our flagship markets with full service availability
              </p>
            </div>

            {loading ? (
              <div className="py-8 text-center">
                <div className="loading loading-spinner loading-lg text-primary"></div>
                <p className="mt-4 text-base-content/70">Loading cities...</p>
              </div>
            ) : featuredCities.length > 0 ? (
              <div className="relative">
                {showSlider && (
                  <>
                    <button
                      className="btn btn-circle btn-sm absolute left-0 top-1/2 z-10 -translate-y-1/2"
                      disabled={featuredSlideIndex === 0}
                      onClick={handlePrevSlide}
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button
                      className="btn btn-circle btn-sm absolute right-0 top-1/2 z-10 -translate-y-1/2"
                      disabled={featuredSlideIndex + 3 >= featuredCities.length}
                      onClick={handleNextSlide}
                    >
                      <ChevronRight size={20} />
                    </button>
                  </>
                )}
                <div className="grid gap-8 lg:grid-cols-3">
                  {visibleFeaturedCities.map((city) => (
                    <div className="card bg-base-100 shadow-xl" key={city.id}>
                      <figure className="h-48">
                        <img
                          alt={city.imageAltText || city.name}
                          className="h-full w-full object-cover"
                          src={`${import.meta.env.VITE_API_STORAGE_URL}/storage/${city.imageUrl}`}
                        />
                      </figure>

                      <div className="card-body">
                        <div className="mb-2 flex items-center justify-between">
                          <h3 className="card-title">{city.name}</h3>
                          <span className="badge">Since {city.sinceYear}</span>
                        </div>

                        <p className="mb-4 text-base-content/70">
                          {city.province} •{" "}
                          {formatPopulation(city.residentsCount, city.unit)}{" "}
                          residents
                        </p>

                        <div className="mb-4">
                          <div className="mb-2 flex flex-wrap gap-1">
                            {city.servicesAvailable.map((service, sIndex) => (
                              <span
                                className="badge badge-sm gap-1"
                                key={sIndex}
                              >
                                {serviceIcons[service.toLowerCase()]}
                                {service.charAt(0).toUpperCase() +
                                  service.slice(1)}
                              </span>
                            ))}
                          </div>
                        </div>

                        <div className="mb-4 grid grid-cols-3 gap-2 text-center text-sm">
                          <div>
                            <div className="font-bold text-primary">
                              {formatNumber(city.totalRides)}
                            </div>
                            <div className="text-xs text-base-content/70">
                              Rides
                            </div>
                          </div>
                          <div>
                            <div className="font-bold text-secondary">
                              {city.restaurants}
                            </div>
                            <div className="text-xs text-base-content/70">
                              Restaurants
                            </div>
                          </div>
                          <div>
                            <div className="font-bold text-success">
                              {city.drivers}
                            </div>
                            <div className="text-xs text-base-content/70">
                              Drivers
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="py-8 text-center">
                <p className="text-base-content/70">
                  No featured cities available at this time.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* All Cities Grid */}
        <section className="bg-base-100 py-16" id="all-cities">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">All Cities</h2>
              <p className="text-xl text-base-content/70">
                Complete list of cities where Hareeng operates
              </p>
            </div>

            {loading ? (
              <div className="py-8 text-center">
                <div className="loading loading-spinner loading-lg text-primary"></div>
                <p className="mt-4 text-base-content/70">Loading cities...</p>
              </div>
            ) : filteredCities.length > 0 ? (
              <>
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {(filteredCities.length > citiesPerPage
                    ? currentCities
                    : filteredCities
                  ).map((city) => (
                    <div className="card bg-base-200 shadow-lg" key={city.id}>
                      <div className="card-body">
                        <div className="mb-2 flex items-center justify-between">
                          <h3 className="card-title text-lg">{city.name}</h3>
                          {city.featured && (
                            <span className="badge badge-sm">Featured</span>
                          )}
                        </div>

                        <p className="mb-3 text-sm text-base-content/70">
                          {city.province} •{" "}
                          {formatPopulation(city.residentsCount, city.unit)}
                        </p>

                        <div className="mb-3 flex flex-wrap gap-1">
                          {city.servicesAvailable.map((service, sIndex) => (
                            <span className="badge badge-xs gap-1" key={sIndex}>
                              {serviceIcons[service.toLowerCase()]}
                              {service.charAt(0).toUpperCase() +
                                service.slice(1)}
                            </span>
                          ))}
                        </div>

                        <div className="text-xs text-base-content/70">
                          Launched: {city.sinceYear}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {filteredCities.length > citiesPerPage && (
                  <div className="mt-12 flex justify-center">
                    <div className="join">
                      <button
                        className="btn join-item btn-sm"
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                      >
                        «
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                        (page) => (
                          <button
                            className={`btn join-item btn-sm ${
                              currentPage === page ? "btn-active" : ""
                            }`}
                            key={page}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </button>
                        ),
                      )}
                      <button
                        className="btn join-item btn-sm"
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                      >
                        »
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="py-8 text-center">
                <p className="text-base-content/70">
                  No cities available at this time.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Coming Soon */}
        <section className="py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">Coming Soon</h2>
              <p className="text-xl text-base-content/70">
                New cities we are planning to launch in
              </p>
            </div>

            {loading ? (
              <div className="py-8 text-center">
                <div className="loading loading-spinner loading-lg text-primary"></div>
                <p className="mt-4 text-base-content/70">
                  Loading upcoming cities...
                </p>
              </div>
            ) : comingSoonCities.length > 0 ? (
              comingSoonCities.length > 4 ? (
                <div className="relative overflow-hidden">
                  <div
                    className="flex gap-6 transition-transform duration-700 ease-in-out"
                    style={{
                      transform: `translateX(-${(comingSoonIndex * 100) / comingSoonCities.length}%)`,
                    }}
                  >
                    {[...comingSoonCities, ...comingSoonCities.slice(0, 4)].map(
                      (request, idx) => (
                        <div
                          className="w-full flex-shrink-0 md:w-1/2 lg:w-1/4"
                          key={`${request.id}-${idx}`}
                        >
                          <div className="card border-2 border-dashed border-primary/30 bg-base-100">
                            <div className="card-body text-center">
                              <h3 className="card-title justify-center">
                                {request.city}
                              </h3>
                              <p className="text-sm text-base-content/70">
                                {request.province}
                              </p>
                              <div className="badge mt-2">
                                Population:{" "}
                                {parseInt(request.population) >= 1000
                                  ? `${(parseInt(request.population) / 1000).toFixed(0)}K`
                                  : request.population}
                              </div>
                            </div>
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                  {/* Carousel indicators */}
                  <div className="mt-6 flex justify-center gap-2">
                    {comingSoonCities.map((_, index) => (
                      <button
                        aria-label={`Go to slide ${index + 1}`}
                        className={`h-2 w-2 rounded-full transition-all ${
                          index === comingSoonIndex % comingSoonCities.length
                            ? "w-8 bg-primary"
                            : "hover:bg-base-400 bg-base-300"
                        }`}
                        key={index}
                        onClick={() => setComingSoonIndex(index)}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                  {comingSoonCities.map((request) => (
                    <div
                      className="card border-2 border-dashed border-primary/30 bg-base-100"
                      key={request.id}
                    >
                      <div className="card-body text-center">
                        <h3 className="card-title justify-center">
                          {request.city}
                        </h3>
                        <p className="text-sm text-base-content/70">
                          {request.province}
                        </p>
                        <div className="badge mt-2">
                          Population:{" "}
                          {parseInt(request.population) >= 1000
                            ? `${(parseInt(request.population) / 1000).toFixed(0)}K`
                            : request.population}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              <div className="py-8 text-center">
                <p className="text-base-content/70">
                  No upcoming cities at this time.
                </p>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
}

// Add shimmer animation to global styles if not present
if (
  typeof document !== "undefined" &&
  !document.getElementById("cities-shimmer-style")
) {
  const style = document.createElement("style");
  style.id = "cities-shimmer-style";
  style.innerHTML = `
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
    .animate-shimmer {
      animation: shimmer 1.5s linear infinite;
    }
  `;
  document.head.appendChild(style);
}
